import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FolderKanban, Search, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatArea, initials } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { ProgressBar } from "@/components/ProgressBar";

export default function Projects() {
  const { meta } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    const { data } = await api.get(`/projects?${params}`);
    setItems(data.items); setLoading(false);
  }, [search, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div><h1 className="text-2xl font-bold tracking-tight text-slate-900">Projects</h1><p className="mt-0.5 text-sm text-slate-500">{items.length} projects · execution, billing & profitability</p></div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="projects-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search projects…" className="h-10 pl-9" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="projects-filter-status" className="h-10 w-48"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{(meta?.project_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-52 rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState testid="projects-empty" icon={FolderKanban} title="No projects yet" description="Convert a Won lead into a project to start tracking phased execution." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => {
            const pr = p.progress;
            return (
              <div key={p.id} data-testid={`project-card-${p.id}`} onClick={() => navigate(`/projects/${p.id}`)} className="group cursor-pointer rounded-xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:border-slate-300 hover:shadow-md">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-xs text-slate-400"><span className="font-mono">{p.project_code}</span></div>
                    <h3 className="truncate text-base font-semibold text-slate-800">{p.name}</h3>
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><Building2 className="h-3 w-3" />{p.company_name}</div>
                  </div>
                  <StatusBadge value={p.status} type="project" dot />
                </div>
                <div className="mt-4">
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-mono font-semibold text-slate-700">{formatArea(pr.completed_area)} / {formatArea(pr.contracted_area)}</span>
                    <span className="font-bold" style={{ color: "#0057A8" }}>{pr.completion_pct}%</span>
                  </div>
                  <ProgressBar pct={pr.completion_pct} />
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-slate-50 pt-3">
                  <span className="text-xs text-slate-500">{p.phase_count} phase{p.phase_count === 1 ? "" : "s"}</span>
                  <span className="font-mono text-sm font-semibold text-slate-800">{formatINR(p.quoted_value)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
